import Link from 'antd/lib/typography/Link';
import * as React from 'react';
import { formatDateWithTimezone } from '../../timeHelpers';
import Divider from 'antd/lib/divider';
import Text from 'antd/lib/typography/Text';
import Tag from 'antd/lib/tag';
import { antGrey, runwayColors, tagStyle } from '../styles/globalStyles';
import { dayjs } from '@/extendedDayjs';
import TimeWithConditionalTz from '@/components/common/TimeWithConditionalTz';
import { TestRunStatus } from '../../types';

const COLLAPSED_PROGRESS_WIDTH = 260;
const STATUS_TAG_WIDTH = 150;
const PROGRESS_BAR_HEIGHT = 8;

const StatusProgressBar: React.FC<{ statuses: TestRunStatus[] }> = ({ statuses }) => (
    <div
        style={{
            display: 'flex',
            flexGrow: 1,
            height: PROGRESS_BAR_HEIGHT,
            borderRadius: PROGRESS_BAR_HEIGHT / 2,
            overflow: 'hidden',
            background: antGrey.grey4,
        }}
    >
        {statuses
            .filter((status) => status.fraction > 0)
            .map((status) => (
                <div
                    key={status.displayableName}
                    style={{ width: `${status.fraction * 100}%`, background: `#${status.color}` }}
                />
            ))}
    </div>
);

const RegressionTestingRunHeader: React.FC<{
    name: string;
    url: string;
    integrationIcon: string;
    integrationIconAlt: string;
    createdAt: Date;
    percentComplete: number;
    completedAt?: Date;
    use24HourTimeFormat: boolean;
    effectiveTimezone: string;
    statuses: TestRunStatus[];
    isExpanded: boolean;
}> = ({
    name,
    url,
    integrationIconAlt,
    integrationIcon,
    completedAt,
    createdAt,
    percentComplete,
    use24HourTimeFormat,
    effectiveTimezone,
    statuses,
    isExpanded,
}) => {
    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                minWidth: 0,
                paddingTop: 8,
                paddingBottom: 8,
            }}
        >
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginRight: 16, minWidth: 0 }}>
                <img src={integrationIcon} alt={integrationIconAlt} width={20} height={20} />
                <Link href={url} target="_blank" strong ellipsis>
                    {name}
                </Link>
                <Text type="secondary" style={{ flexShrink: 0 }}>
                    -{' '}
                    {completedAt ?
                        <>
                            completed{' '}
                            <TimeWithConditionalTz
                                triggeredAtLabel={formatDateWithTimezone(
                                    completedAt,
                                    'short',
                                    effectiveTimezone,
                                    use24HourTimeFormat,
                                )}
                                triggeredByMoment={dayjs(completedAt)}
                                use24HourTimeFormat={use24HourTimeFormat}
                                effectiveTimezone={effectiveTimezone}
                                style={{ color: 'inherit' }}
                            />
                        </>
                    :   <>
                            started{' '}
                            <TimeWithConditionalTz
                                triggeredAtLabel={formatDateWithTimezone(
                                    createdAt,
                                    'short',
                                    effectiveTimezone,
                                    use24HourTimeFormat,
                                )}
                                triggeredByMoment={dayjs(createdAt)}
                                use24HourTimeFormat={use24HourTimeFormat}
                                effectiveTimezone={effectiveTimezone}
                                style={{ color: 'inherit' }}
                            />
                        </>
                    }
                </Text>
                <Divider type="vertical" />
                <Text type="secondary" style={{ flexShrink: 0 }}>
                    {percentComplete}% tested
                </Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {!isExpanded && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: COLLAPSED_PROGRESS_WIDTH }}>
                        <Text type="secondary" style={{ flexShrink: 0 }}>
                            {percentComplete}% complete
                        </Text>
                        <StatusProgressBar statuses={statuses} />
                    </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', width: STATUS_TAG_WIDTH }}>
                    <Tag
                        color={completedAt ? runwayColors.statusGreen : runwayColors.statusYellow}
                        style={{ ...tagStyle, fontSize: 10, marginInlineEnd: 0 }}
                    >
                        {completedAt ? 'TEST RUN COMPLETE' : 'TEST RUN ACTIVE'}
                    </Tag>
                </div>
            </div>
        </div>
    );
};

export default RegressionTestingRunHeader;
